import type { Kernel, RNG } from "../sdk/kernel";

interface PiPartial {
  hits: number;
  count: number;
}

// π by sampling — the teaching kernel. Uses only +, *, comparison and the SDK rng:
// pure IEEE-bitwise-specified ops, so it reproduces bitwise across V8 / SpiderMonkey /
// JavaScriptCore with no math shim (Spike 02 Day 2, threshold #7 PASS).
export const pi: Kernel<PiPartial> = {
  id: "pi",
  label: "π by sampling",
  verifiable: true,
  blurb: "Estimate π by sampling points in the unit square. Arithmetic-only — reproduces bitwise across engines with no math shim.",
  resultUnit: "π estimate",
  defaultN: 1_000_000,
  chunkSize: 25_000, // ~40 chunks at default N — a readable per-chunk distribution

  chunk(count: number, rng: RNG): PiPartial {
    let hits = 0;
    for (let i = 0; i < count; i++) {
      const x = rng.float();
      const y = rng.float();
      if (x * x + y * y < 1) hits++;
    }
    return { hits, count };
  },

  reduce(partials: PiPartial[]) {
    // Integer-count reduction in fixed order: result is independent of chunking.
    let hits = 0;
    let count = 0;
    const samples: number[] = [];
    for (const p of partials) {
      hits += p.hits;
      count += p.count;
      samples.push(p.count > 0 ? (4 * p.hits) / p.count : 0); // per-chunk estimate
    }
    return { result: count > 0 ? (4 * hits) / count : 0, samples };
  },
};
